import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import api, { API_ENDPOINTS } from '../config/api';
import { 
  ArrowLeft, 
  DollarSign, 
  MapPin, 
  Clock, 
  Tag, 
  FileText,
  Upload,
  Plus,
  X
} from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import ProfessionalRichTextEditor from '../components/ProfessionalRichTextEditor';
import { JOB_CATEGORIES, formatCategoryName, formatSubCategoryName } from '../constants/categories';

const CreateJobPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [requirements, setRequirements] = useState(['']);
  const [deliverables, setDeliverables] = useState(['']);
  const [skills, setSkills] = useState(['']);
  const [budgetType, setBudgetType] = useState('fixed');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [location, setLocation] = useState({
    remote: false,
    city: '',
    country: ''
  });

  // Create job mutation with FormData for optional file upload
  const createJobMutation = useMutation(
    (formData) => {
      return api.post(API_ENDPOINTS.JOBS.CREATE, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    {
      onSuccess: () => {
        toast.success('Job created successfully!');
        queryClient.invalidateQueries(['jobs']);
        navigate('/jobs');
      },
      onError: (error) => {
        const errorData = error.response?.data;
        if (errorData?.errors && Array.isArray(errorData.errors)) {
          // Handle validation errors
          errorData.errors.forEach(err => {
            toast.error(err.msg || err.message || 'Validation error');
          });
        } else if (errorData?.error) {
          toast.error(errorData.error);
        } else {
          toast.error('Failed to create job. Please check all required fields are filled correctly.');
        }
      }
    }
  );

  const { register, handleSubmit, formState: { errors }, watch, control, setValue } = useForm();
  
  // Watch category to update selectedCategory state
  const watchedCategory = watch('category');
  useEffect(() => {
    if (watchedCategory) {
      setSelectedCategory(watchedCategory);
    } else {
      setSelectedCategory('');
      setSelectedSubCategory('');
    }
  }, [watchedCategory]);

  const addRequirement = () => {
    setRequirements([...requirements, '']);
  };

  const removeRequirement = (index) => {
    if (requirements.length > 1) {
      setRequirements(requirements.filter((_, i) => i !== index));
    }
  };

  const updateRequirement = (index, value) => {
    const newRequirements = [...requirements];
    newRequirements[index] = value;
    setRequirements(newRequirements);
  };

  const addDeliverable = () => {
    setDeliverables([...deliverables, '']);
  };

  const removeDeliverable = (index) => {
    if (deliverables.length > 1) {
      setDeliverables(deliverables.filter((_, i) => i !== index));
    }
  };

  const updateDeliverable = (index, value) => {
    const newDeliverables = [...deliverables];
    newDeliverables[index] = value;
    setDeliverables(newDeliverables);
  };

  const addSkill = () => {
    setSkills([...skills, '']);
  };

  const removeSkill = (index) => {
    if (skills.length > 1) {
      setSkills(skills.filter((_, i) => i !== index));
    }
  };

  const updateSkill = (index, value) => {
    const newSkills = [...skills];
    newSkills[index] = value;
    setSkills(newSkills);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = (data) => {
    console.log('Form data:', data);
    console.log('Selected category:', selectedCategory);
    console.log('Category from form:', data.category);
    
    // Validate category is selected
    if (!data.category || data.category === '') {
      toast.error('Please select a category');
      return;
    }

    // Validate description has content
    if (!data.description || data.description.trim() === '' || data.description.replace(/<[^>]*>/g, '').trim() === '') {
      toast.error('Please provide a description with actual content');
      return;
    }

    // Build budget object based on budget type
    let budget = {};
    if (budgetType === 'range') {
      if (data.budgetMin && data.budgetMax) {
        budget = {
          min: parseFloat(data.budgetMin),
          max: parseFloat(data.budgetMax)
        };
      }
    } else {
      budget = {
        [budgetType]: parseFloat(data.budgetAmount)
      };
    }

    // Create FormData for optional file upload
    const formData = new FormData();
    
    // Append all text fields
    formData.append('title', data.title);
    formData.append('description', data.description);
    formData.append('category', data.category);
    if (data.subCategory) {
      formData.append('subCategory', data.subCategory);
    }
    formData.append('type', data.type);
    formData.append('budget', JSON.stringify(budget));
    formData.append('location', JSON.stringify(location));
    
    // Append arrays
    formData.append('requirements', JSON.stringify(requirements.filter(req => req.trim() !== '')));
    formData.append('deliverables', JSON.stringify(deliverables.filter(del => del.trim() !== '')));
    formData.append('skills', JSON.stringify(skills.filter(skill => skill.trim() !== '')));
    
    // Append duration
    if (data.durationValue && data.durationUnit) {
      formData.append('duration', JSON.stringify({
        value: parseInt(data.durationValue),
        unit: data.durationUnit
      }));
    }

    // Append image file if provided (optional for jobs)
    if (selectedImage) {
      formData.append('image', selectedImage);
    }

    createJobMutation.mutate(formData);
  };

  const jobTypes = [
    'full-time',
    'part-time',
    'freelance',
    'contract'
  ];

  const durationUnits = ['hours', 'days', 'weeks', 'months'];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-accent mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>
        
        <h1 className="text-3xl font-heading font-bold text-heading mb-2">
          Post a New Job
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Create a job posting to find the right talent for your project.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="max-w-4xl">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Basic Information
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Job Title *
                  </label>
                  <input
                    {...register('title', { required: 'Job title is required' })}
                    type="text"
                    className="w-full input-field"
                    placeholder="e.g., Senior React Developer"
                  />
                  {errors.title && (
                    <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Job Description *
                  </label>
                  <Controller
                    name="description"
                    control={control}
                    rules={{ required: 'Job description is required' }}
                    render={({ field }) => (
                      <ProfessionalRichTextEditor
                        value={field.value || ''}
                        onChange={field.onChange}
                        placeholder="Describe the job requirements, responsibilities, and what you're looking for. You can format text, add headings, links, images, videos, and tables..."
                      />
                    )}
                  />
                  {errors.description && (
                    <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Category *
                    </label>
                    <Controller
                      name="category"
                      control={control}
                      rules={{ required: 'Category is required' }}
                      render={({ field }) => (
                        <select
                          {...field}
                          className="w-full input-field"
                          onChange={(e) => {
                            field.onChange(e);
                            setSelectedCategory(e.target.value);
                            setSelectedSubCategory('');
                            setValue('subCategory', '');
                          }}
                        >
                          <option value="">Select a category</option>
                          {Object.entries(JOB_CATEGORIES).map(([key, category]) => (
                            <option key={key} value={key}>
                              {category.label}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                    {errors.category && (
                      <p className="text-red-500 text-sm mt-1">{errors.category.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Sub-Category
                    </label>
                    <Controller
                      name="subCategory"
                      control={control}
                      render={({ field }) => (
                        <select
                          {...field}
                          className="w-full input-field"
                          disabled={!selectedCategory || !watch('category')}
                          onChange={(e) => {
                            field.onChange(e);
                            setSelectedSubCategory(e.target.value);
                          }}
                        >
                          <option value="">Select a sub-category (optional)</option>
                          {selectedCategory && JOB_CATEGORIES[selectedCategory]?.subCategories.map(subCat => (
                            <option key={subCat} value={subCat}>
                              {formatSubCategoryName(subCat)}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Job Type *
                  </label>
                  <select
                    {...register('type', { required: 'Job type is required' })}
                    className="w-full input-field"
                  >
                    <option value="">Select job type</option>
                    {jobTypes.map(type => (
                      <option key={type} value={type}>
                        {type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </option>
                    ))}
                  </select>
                  {errors.type && (
                    <p className="text-red-500 text-sm mt-1">{errors.type.message}</p>
                  )}
                </div>

                {/* Job Image Upload - Optional */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Job Image (Optional)
                  </label>
                  <div className="space-y-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-accent file:text-white hover:file:bg-accent-dark"
                    />
                    {imagePreview && (
                      <div className="mt-2">
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          className="w-full max-w-md h-48 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
                        />
                      </div>
                    )}
                    {!selectedImage && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Upload an image to showcase your job posting (Max 5MB)
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Budget */}
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Budget
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Budget Type
                  </label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="fixed"
                        checked={budgetType === 'fixed'}
                        onChange={(e) => setBudgetType(e.target.value)}
                        className="mr-2"
                      />
                      Fixed Price
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="range"
                        checked={budgetType === 'range'}
                        onChange={(e) => setBudgetType(e.target.value)}
                        className="mr-2"
                      />
                      Budget Range
                    </label>
                  </div>
                </div>

                {budgetType === 'range' ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Minimum Budget ($) *
                      </label>
                      <input
                        {...register('budgetMin', { 
                          required: budgetType === 'range' ? 'Minimum budget is required' : false,
                          min: { value: 1, message: 'Budget must be at least 1' },
                          validate: (value) => !value || Number.isInteger(Number(value)) || 'Budget must be a whole number'
                        })}
                        type="number"
                        min="1"
                        className="w-full input-field"
                        placeholder="1000"
                        onChange={(e) => {
                          const value = e.target.value;
                          // Only allow positive integers
                          if (value === '' || (Number.isInteger(Number(value)) && Number(value) >= 1)) {
                            const { onChange } = register('budgetMin');
                            onChange(e);
                          }
                        }}
                      />
                      {errors.budgetMin && (
                        <p className="text-red-500 text-sm mt-1">{errors.budgetMin.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Maximum Budget ($) *
                      </label>
                      <input
                        {...register('budgetMax', { 
                          required: budgetType === 'range' ? 'Maximum budget is required' : false,
                          min: { value: 1, message: 'Budget must be at least 1' },
                          validate: (value) => {
                            if (!value) return true;
                            if (!Number.isInteger(Number(value))) return 'Budget must be a whole number';
                            const min = watch('budgetMin');
                            if (budgetType === 'range' && min && parseFloat(value) <= parseFloat(min)) {
                              return 'Maximum budget must be greater than minimum budget';
                            }
                            return true;
                          }
                        })}
                        type="number"
                        min="1"
                        className="w-full input-field"
                        placeholder="5000"
                        onChange={(e) => {
                          const value = e.target.value;
                          // Only allow positive integers
                          if (value === '' || (Number.isInteger(Number(value)) && Number(value) >= 1)) {
                            const { onChange } = register('budgetMax');
                            onChange(e);
                          }
                        }}
                      />
                      {errors.budgetMax && (
                        <p className="text-red-500 text-sm mt-1">{errors.budgetMax.message}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Fixed Price ($) *
                    </label>
                    <input
                      {...register('budgetAmount', { 
                        required: 'Budget amount is required',
                        min: { value: 1, message: 'Budget must be at least 1' },
                        validate: (value) => Number.isInteger(Number(value)) || 'Budget must be a whole number'
                      })}
                      type="number"
                      min="1"
                      className="w-full input-field"
                      placeholder="5000"
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only allow positive integers
                        if (value === '' || (Number.isInteger(Number(value)) && Number(value) >= 1)) {
                          const { onChange } = register('budgetAmount');
                          onChange(e);
                        }
                      }}
                    />
                    {errors.budgetAmount && (
                      <p className="text-red-500 text-sm mt-1">{errors.budgetAmount.message}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Duration */}
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Duration
              </h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Duration Value
                  </label>
                  <input
                    {...register('durationValue', {
                      validate: (value) => !value || (Number.isInteger(Number(value)) && Number(value) >= 1) || 'Duration must be a whole number of at least 1'
                    })}
                    type="number"
                    min="1"
                    className="w-full input-field"
                    placeholder="3"
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only allow positive integers
                      if (value === '' || (Number.isInteger(Number(value)) && Number(value) >= 1)) {
                        const { onChange } = register('durationValue');
                        onChange(e);
                      }
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Duration Unit
                  </label>
                  <select
                    {...register('durationUnit')}
                    className="w-full input-field"
                  >
                    <option value="">Select unit</option>
                    {durationUnits.map(unit => (
                      <option key={unit} value={unit}>
                        {unit.charAt(0).toUpperCase() + unit.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Requirements */}
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Requirements
              </h2>
              
              <div className="space-y-3">
                {requirements.map((req, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={req}
                      onChange={(e) => updateRequirement(index, e.target.value)}
                      className="flex-1 input-field"
                      placeholder="Enter a requirement..."
                    />
                    {requirements.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeRequirement(index)}
                        className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addRequirement}
                  className="flex items-center text-accent hover:text-accent/80"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Requirement
                </button>
              </div>
            </div>

            {/* Deliverables */}
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Deliverables
              </h2>
              
              <div className="space-y-3">
                {deliverables.map((del, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={del}
                      onChange={(e) => updateDeliverable(index, e.target.value)}
                      className="flex-1 input-field"
                      placeholder="Enter a deliverable..."
                    />
                    {deliverables.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeDeliverable(index)}
                        className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addDeliverable}
                  className="flex items-center text-accent hover:text-accent/80"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Deliverable
                </button>
              </div>
            </div>

            {/* Skills */}
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Required Skills
              </h2>
              
              <div className="space-y-3">
                {skills.map((skill, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={skill}
                      onChange={(e) => updateSkill(index, e.target.value)}
                      className="flex-1 input-field"
                      placeholder="Enter a skill..."
                    />
                    {skills.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSkill(index)}
                        className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addSkill}
                  className="flex items-center text-accent hover:text-accent/80"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Skill
                </button>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Location */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Location
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={location.remote}
                      onChange={(e) => setLocation(prev => ({ ...prev, remote: e.target.checked }))}
                      className="rounded border-gray-300 text-accent focus:ring-accent"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Remote work available</span>
                  </label>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    City (optional)
                  </label>
                  <input
                    type="text"
                    value={location.city}
                    onChange={(e) => setLocation(prev => ({ ...prev, city: e.target.value }))}
                    className="w-full input-field"
                    placeholder="e.g., New York"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Country (optional)
                  </label>
                  <input
                    type="text"
                    value={location.country}
                    onChange={(e) => setLocation(prev => ({ ...prev, country: e.target.value }))}
                    className="w-full input-field"
                    placeholder="e.g., United States"
                  />
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Preview
              </h3>
              
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <p>Your job will be visible to talents once published.</p>
                <p className="mt-2">You can edit or delete it anytime from your dashboard.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="mt-8 flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn-outline"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createJobMutation.isLoading}
            className="btn-primary"
          >
            {createJobMutation.isLoading ? 'Creating...' : 'Post Job'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateJobPage;